import React, { Component } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import * as Sentry from '@sentry/react';

const ErrorContainer = styled.div`
  padding: 2rem;
  background: #fee2e2;
  border: 1px solid #fca5a5;
  border-radius: 0.5rem;
  color: #991b1b;
  margin: 2rem auto;
  max-width: 800px;
`;

const ErrorTitle = styled.h2`
  color: #b91c1c;
  margin-top: 0;
`;

const ErrorDetails = styled.pre`
  background: white;
  padding: 1rem;
  border-radius: 0.25rem;
  overflow-x: auto;
  font-size: 0.875rem;
`;

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error,
      errorInfo,
    });

    // Log error to console in development
    if (process.env.NODE_ENV !== 'production') {
      console.error('Error caught by ErrorBoundary:', error, errorInfo);
    }

    // Log error to Sentry
    try {
      Sentry.captureException(error, {
        extra: {
          componentStack: errorInfo?.componentStack,
        },
      });
    } catch (sentryError) {
      console.error('Failed to send error to Sentry:', sentryError);
    }
  }

  render() {
    const { hasError, error, errorInfo } = this.state;
    const { FallbackComponent } = this.props;

    if (hasError) {
      if (FallbackComponent) {
        return <FallbackComponent error={error} errorInfo={errorInfo} />;
      }

      return (
        <ErrorContainer>
          <ErrorTitle>Something went wrong.</ErrorTitle>
          <p>
            The application encountered an error. Please refresh the page or try
            again later.
          </p>
          {process.env.NODE_ENV === 'development' && (
            <div>
              <h4>Error Details:</h4>
              <ErrorDetails>
                {this.state.error && this.state.error.toString()}
                <br />
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </ErrorDetails>
            </div>
          )}
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  FallbackComponent: PropTypes.func,
};

ErrorBoundary.defaultProps = {
  FallbackComponent: null,
};

export default ErrorBoundary;
